import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useGetPublicStatisticsQuery, useGetPublicProjectsQuery } from '../store/slices/apiSlice';
import {
  Users,
  TrendingUp,
  MapPin,
  Building2,
  Shield,
  Award,
  Target,
  Briefcase,
  Calendar,
  Globe,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';

const UKNigerianDiaspora: React.FC = () => {
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

  // Fetch real statistics and projects
  const { data: statsData } = useGetPublicStatisticsQuery();
  const { data: projectsResponse } = useGetPublicProjectsQuery();
  const projects = Array.isArray(projectsResponse?.data) ? projectsResponse?.data :
    Array.isArray(projectsResponse?.data?.projects) ? projectsResponse?.data?.projects : [];

  // Update page title and meta description
  useEffect(() => {
    document.title = "Nigerian Diaspora Investment in the UK | Build Wealth, Invest in Nigeria";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Join 150,000+ Nigerian professionals in the UK investing in Nigeria's future. Explore real estate, agriculture, renewable energy & tech opportunities with secure platforms.");
    }
  }, []);

  // Format currency helper
  const formatCurrency = (amount: number) => {
    if (amount >= 1000000000) {
      return `$${(amount / 1000000000).toFixed(1)}B+`;
    } else if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M+`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K+`;
    }
    return `$${Math.round(amount).toLocaleString()}`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M+`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K+`;
    }
    return num.toString();
  };

  // Calculate real stats
  const totalUsers = statsData?.data?.total_users || 0;
  const totalInvestments = statsData?.data?.total_investments || 0;
  const totalProjects = statsData?.data?.total_projects || projects.length;
  const totalAmountInvested = statsData?.data?.total_amount_invested || 0;

  // Extract unique cities from projects
  const projectCities = useMemo(() => {
    const cityMap = new Map<string, number>();
    projects.forEach((project: any) => {
      if (project.location) {
        const city = project.location.split(',')[0].trim();
        cityMap.set(city, (cityMap.get(city) || 0) + 1);
      }
    });
    return Array.from(cityMap.entries())
      .map(([name, count]) => ({
        name,
        investors: `${(count * 10).toLocaleString()}+`, // Estimate investors per project
        description: `${count} active project${count > 1 ? 's' : ''} in this city`
      }))
      .slice(0, 6); // Limit to 6 cities
  }, [projects]);

  const faqs = [
    {
      id: 1,
      question: "What investment opportunities are available for the Nigerian diaspora in the UK?",
      answer: "Nigerians in the UK can invest in real estate, agriculture, technology, infrastructure, and small businesses through secure DiasporaWise projects."
    },
    {
      id: 2,
      question: "How can I send investment funds from the UK to Nigeria safely?",
      answer: "DiasporaWise provides regulated, transparent, and secure payment channels, ensuring your funds are directly invested into approved projects."
    },
    {
      id: 3,
      question: "Do I need to travel to Nigeria to manage my investments?",
      answer: "No, you can manage and monitor your investments online from the UK, with full transparency and project updates."
    },
    {
      id: 4,
      question: "What returns can UK-based Nigerians expect on investments?",
      answer: "Returns vary depending on the project type, but DiasporaWise ensures risk-assessed, profitable, and transparent investment opportunities."
    },
    {
      id: 5,
      question: "Is investing in Nigeria from the UK safe?",
      answer: "Yes, all investments are vetted and secured through DiasporaWise's trusted platform, ensuring compliance, transparency, and accountability."
    }
  ];



  // Use real cities from projects, or fallback to default if no projects
  const cities = projectCities.length > 0 ? projectCities : [
    { name: "London", investors: "85,000+", description: "The largest concentration of Nigerian professionals in the UK" },
    { name: "Manchester", investors: "25,000+", description: "Growing tech and business hub for Nigerian professionals" },
    { name: "Birmingham", investors: "18,000+", description: "Strong community with diverse professional backgrounds" },
    { name: "Leeds", investors: "8,000+", description: "Emerging center for Nigerian entrepreneurs and investors" },
    { name: "Liverpool", investors: "6,000+", description: "Historic city with growing Nigerian business community" },
    { name: "Bristol", investors: "5,000+", description: "Tech and innovation hub attracting Nigerian talent" }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "UK Tax Optimization",
      description: "Expert guidance on UK tax implications and optimization strategies for your Nigerian investments."
    },
    {
      icon: Globe,
      title: "Currency Exchange",
      description: "Competitive rates and secure methods for transferring funds from GBP to Naira."
    },
    {
      icon: Award,
      title: "Local Support",
      description: "Dedicated UK-based team available during UK business hours for personalized support."
    },
    {
      icon: Users,
      title: "Community Network",
      description: "Connect with successful Nigerian professionals across the UK for networking and collaboration."
    }
  ];

  const investmentTypes = [
    {
      title: "Real Estate",
      description: "Premium properties in Lagos, Abuja, and other major cities",
      returns: "15-25%",
      icon: Building2
    },
    {
      title: "Agriculture",
      description: "Modern farming and agribusiness opportunities",
      returns: "12-20%",
      icon: TrendingUp
    },
    {
      title: "Technology",
      description: "Startups and tech companies with high growth potential",
      returns: "20-40%",
      icon: Target
    },
    {
      title: "Infrastructure",
      description: "Government and private infrastructure development projects",
      returns: "10-18%",
      icon: Briefcase
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Simple Hero Section */}
      <section className="relative py-12 px-6 bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-sm font-medium mb-6 text-gray-600 border border-gray-200">
              <MapPin className="w-4 h-4" />
              United Kingdom
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Nigerian Diaspora Investment in the UK
            </h1>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8">
              Join {formatNumber(Math.floor(totalUsers * 0.2))} Nigerian professionals in the UK investing in Nigeria's future. Explore real estate, agriculture, renewable energy & tech opportunities with secure platforms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.diasporawise.com"
                className="bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-900 transition-all duration-200 flex items-center justify-center gap-2 lg:hidden"
              >
                <Briefcase className="w-5 h-5" />
                Start Investing Today
              </a>
              <Link
                to="/contact"
                className="bg-white border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Contact UK Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* UK Cities */}
      <section className="py-20 px-6" style={{ backgroundColor: '#eff6ff' }}>
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nigerian Communities Across the UK
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Connect with Nigerian professionals in major UK cities and access local networking opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cities.map((city, index) => (
              <div key={index} className="rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-blue-200" style={{ backgroundColor: '#f8fafc' }}>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{city.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{city.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Investment Opportunities */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(to bottom right, #eff6ff, #dbeafe)' }}>
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Investment Opportunities for UK Investors
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Access to carefully curated investment opportunities with UK-specific considerations and support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {investmentTypes.map((investment, index) => (
              <div key={index} className="text-center">
                <div className="rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-blue-200" style={{ backgroundColor: '#f8fafc' }}>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <investment.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{investment.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{investment.description}</p>
                  <div className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold">
                    {investment.returns} Returns
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UK-Specific Benefits */}
      <section className="py-20 px-6" style={{ backgroundColor: '#eff6ff' }}>
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              UK-Specific Benefits & Support
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tailored services and support designed specifically for UK-based Nigerian investors.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-blue-200" style={{ backgroundColor: '#f8fafc' }}>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UK Events & Meetups */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(to bottom right, #eff6ff, #dbeafe)' }}>
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Upcoming UK Events
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join our networking events and investment seminars across the UK.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                date: "March 15, 2024",
                title: "London Investment Summit",
                location: "London, UK",
                description: "Connect with fellow investors and learn about new opportunities"
              },
              {
                date: "March 22, 2024",
                title: "Manchester Tech Investment Meetup",
                location: "Manchester, UK",
                description: "Focus on technology and startup investment opportunities"
              },
              {
                date: "March 29, 2024",
                title: "Birmingham Real Estate Seminar",
                location: "Birmingham, UK",
                description: "Learn about real estate investment strategies and market trends"
              }
            ].map((event, index) => (
              <div key={index} className="rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-blue-200" style={{ backgroundColor: '#f8fafc' }}>
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-600 font-semibold">{event.date}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">{event.location}</span>
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">{event.description}</p>
                <button className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-300">
                  Register Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Invest from the UK?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of successful Nigerian professionals in the UK who are building wealth through strategic investments in Nigeria.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.diasporawise.com"
              className="bg-white text-blue-700 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg flex items-center justify-center gap-2 lg:hidden"
            >
              <Target className="w-5 h-5" />
              Get Started Now
            </a>
            <Link
              to="/contact"
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              Contact UK Team
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about investing in Nigeria from the UK"
        faqs={faqs}
      />

      {/* Learn More About Nigerian Diaspora Investment in the UK Section */}
      <section className="bg-white py-12 px-4 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <button
              onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
              className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
              aria-expanded={isLearnMoreOpen}
              aria-controls="uk-diaspora-content"
            >
              <span className="font-medium">
                {isLearnMoreOpen ? 'Hide Learn More About Nigerian Diaspora Investment in the UK' : 'Learn More About Nigerian Diaspora Investment in the UK'}
              </span>
              {isLearnMoreOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          <div
            id="uk-diaspora-content"
            className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <article className="prose prose-lg max-w-none">
              {/* Main Content Section */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Nigerian Diaspora Investment in the UK: Building Wealth and Driving Nigeria's Future
                </h2>
              </div>

              {/* Section 1 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Introduction: Nigerians Living in the UK and Why They Are Investing at Home</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With a population of over 150,000 Nigerian people living in the UK, it is among the largest diaspora groups. Although remittances have supported families in Nigeria for generations, they do not usually translate into permanent wealth or sustainable development.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This is where the potential of Nigerian diaspora investment comes into play. Rather than limiting themselves to money transfers, UK-based Nigerians can now invest in Nigeria from the UK on structured, safe, and transparent platforms like Diasporawise.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  These opportunities not only create wealth for investors but also generate jobs, build infrastructure, and contribute to national progress. For UK Nigerian professionals, investing is more than just finance—it's about holding onto heritage, ensuring financial security, and supporting Nigeria's sustainable prosperity.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Understanding Nigerian Diaspora Investment from the UK</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Nigerian diaspora investment initiative has been described as "the bridge between the rich resources of Nigeria and the talent of our sons and daughters abroad."
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  It offers tailored diaspora investment opportunities for Nigerians outside Nigeria—particularly those in the UK—to invest funds into carefully vetted projects at home.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Unlike remittances, which cover immediate needs, diaspora investments focus on creating long-term assets and economic development.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Through Diasporawise, Nigerian diaspora investors in the UK can access: Real estate projects with consistent rental income. Agriculture businesses with strong export potential. Technology startups driving innovation in Africa. Renewable energy projects addressing Nigeria's power needs. SMEs and infrastructure that strengthen local industries.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This transition from remitting money to investing in Nigeria safely is giving UK Nigerians the chance to build wealth while contributing to sustainable development at home.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Top Sectors for Nigerians in the UK to Invest In</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Real Estate Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Real estate is one of the safest and most profitable diaspora investment opportunities. The demand for affordable housing, modern settlements, and commercial buildings is rising across Nigeria.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  UK Nigerians can explore: Residential estates and rental apartments. Office complexes and shopping centers. Land investments with long-term value growth.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Real estate investment in Nigeria allows UK investors to benefit from steady rental income while also enjoying asset appreciation.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Agriculture Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Agriculture remains central to Nigeria's economy and offers lucrative opportunities for the diaspora.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  UK Nigerians can invest in: Large-scale crop production. Food processing and packaging ventures. Export-oriented agribusiness.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Agriculture investment in Nigeria ensures food security, generates employment, and delivers strong financial returns—making it attractive for UK Nigerian professionals.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Technology & Startups</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's technology industry is among the fastest-growing in Africa, gaining global prominence through fintech and health-tech.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  By pursuing technology investment in Nigeria, UK Nigerians can: Support early-stage startups. Back scalable digital solutions. Gain high financial returns while supporting innovation.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This is one of the strongest opportunities for Nigerian diaspora investors in the UK.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Renewable Energy Investment in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With ongoing energy shortages, renewable energy investment in Nigeria is a critical growth area.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diaspora investors can participate in: Solar energy startups. Wind and clean energy projects. Rural electrification initiatives.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  These projects generate financial returns while also creating social impact, making them ideal for UK Nigerian professionals.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">SMEs and Infrastructure</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  SMEs are the backbone of Nigeria's economy, while infrastructure supports national growth.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  By investing in SMEs in Nigeria and infrastructure development, UK diaspora investors can: Generate jobs. Strengthen industries. Expand Nigeria's economic base.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  These sectors are impactful and provide sustainable long-term rewards.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Benefits of Diaspora Investment for UK Nigerians</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For Nigerians in the UK, diaspora investment opportunities offer multiple benefits:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Wealth Creation</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Build lasting assets instead of relying only on remittances.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Safe Investments in Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Platforms like Diasporawise ensure transparency and regulation.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nation Building</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investments fuel job creation, infrastructure, and development.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Cultural Connection</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Maintain ties with Nigeria while living abroad.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Generational Wealth</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Secure financial stability for future generations.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For UK Nigerian professionals, this is both a financial strategy and a patriotic responsibility.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">5. How to Start Investing in Nigeria from the UK</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Technology has made it easy to invest in Nigeria from the UK. With diaspora-focused platforms, the process is now safe, transparent, and simple.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Steps to start:</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  1. Select a licensed platform like Diasporawise.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  2. Choose your preferred sector—real estate, agriculture, technology, renewable energy, or SMEs.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  3. Review risk and return profiles.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  4. Invest in Nigeria safely online using secure payment methods.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  5. Track your investments with live updates and detailed reports.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With such platforms, Nigerian diaspora investors in the UK no longer worry about stranded funds or unreliable intermediaries.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Future of Nigerian Diaspora Investment in the UK</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The outlook for Nigerian diaspora investment is very promising. Key drivers include:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fintech platforms like Diasporawise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Making cross-border investments seamless.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Green energy projects</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Pushing sustainable development.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Government incentives</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Encouraging diaspora engagement.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Regional networks</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Linking UK Nigerian professionals to tailored opportunities.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In the coming decade, remittances will take a backseat as Nigerian diaspora investors in the UK become central to driving Nigeria's economic growth.
                </p>
              </div>

              {/* Conclusion */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Conclusion: Building Wealth and Strengthening Nigeria</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For the Nigerian diaspora in the UK, investment is about more than money—it's about shaping Nigeria's future. By exploring safe investments in Nigeria through real estate, agriculture, technology, renewable energy, and SMEs, UK Nigerians can secure wealth, support development, and leave a legacy for generations.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  It's time to move beyond remittances and start investing in Nigeria safely. Join the community of UK Nigerian professionals already transforming Nigeria's economy through Diasporawise—the trusted platform for global diaspora investments.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UKNigerianDiaspora;
